import * as React from 'react';
import { clsx } from 'clsx';

// ============================================================================
// TYPES
// ============================================================================

export interface ContextMenuItemBase {
  id: string;
  type?: 'item' | 'separator' | 'submenu';
}

export interface ContextMenuSeparator extends ContextMenuItemBase {
  type: 'separator';
}

export interface ContextMenuItem extends ContextMenuItemBase {
  type?: 'item';
  label: string;
  icon?: React.ReactNode;
  shortcut?: string;
  disabled?: boolean;
  danger?: boolean;
  onClick?: () => void;
}

export interface ContextMenuSubmenu extends ContextMenuItemBase {
  type: 'submenu';
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  items: ContextMenuItemType[];
}

export type ContextMenuItemType = ContextMenuItem | ContextMenuSeparator | ContextMenuSubmenu;

export interface ContextMenuPosition {
  x: number;
  y: number;
}

// ============================================================================
// CONTEXT MENU ITEM COMPONENT
// ============================================================================

interface ContextMenuItemComponentProps {
  item: ContextMenuItem;
  onSelect: (item: ContextMenuItem) => void;
}

function ContextMenuItemComponent({ item, onSelect }: ContextMenuItemComponentProps) {
  const handleClick = () => {
    if (!item.disabled) {
      item.onClick?.();
      onSelect(item);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={item.disabled}
      className={clsx(
        'w-full flex items-center justify-between gap-4 px-3 py-2 text-sm text-left transition-colors',
        item.disabled
          ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
          : item.danger
          ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
      )}
    >
      <span className="flex items-center gap-2">
        {item.icon && <span className="w-4 h-4 flex-shrink-0">{item.icon}</span>}
        <span>{item.label}</span>
      </span>
      {item.shortcut && (
        <span
          className={clsx(
            'text-xs',
            item.disabled
              ? 'text-gray-300 dark:text-gray-700'
              : 'text-gray-400 dark:text-gray-500'
          )}
        >
          {item.shortcut}
        </span>
      )}
    </button>
  );
}

// ============================================================================
// CONTEXT MENU SEPARATOR COMPONENT
// ============================================================================

function ContextMenuSeparatorComponent() {
  return (
    <div
      className="my-1 h-px bg-gray-200 dark:bg-gray-700"
      role="separator"
      aria-orientation="horizontal"
    />
  );
}

// ============================================================================
// CONTEXT MENU SUBMENU COMPONENT
// ============================================================================

interface ContextMenuSubmenuComponentProps {
  item: ContextMenuSubmenu;
  onSelect: (item: ContextMenuItem) => void;
  onClose: () => void;
}

function ContextMenuSubmenuComponent({
  item,
  onSelect,
  onClose,
}: ContextMenuSubmenuComponentProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [position, setPosition] = React.useState({ top: 0, left: 0 });
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const submenuRef = React.useRef<HTMLDivElement>(null);
  const timeoutRef = React.useRef<NodeJS.Timeout>();

  const openSubmenu = () => {
    if (item.disabled) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setIsOpen(true), 100);
  };

  const closeSubmenu = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setIsOpen(false), 100);
  };

  React.useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const submenuWidth = 200; // Estimated width
      const submenuHeight = item.items.length * 36; // Estimated height

      let left = rect.right;
      let top = rect.top;

      // Adjust for viewport boundaries
      if (left + submenuWidth > viewportWidth) {
        left = rect.left - submenuWidth;
      }
      if (top + submenuHeight > viewportHeight) {
        top = viewportHeight - submenuHeight - 8;
      }
      if (top < 0) top = 8;

      setPosition({ top, left });
    }
  }, [isOpen, item.items.length]);

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleSubmenuSelect = (selectedItem: ContextMenuItem) => {
    onSelect(selectedItem);
    setIsOpen(false);
  };

  return (
    <div
      ref={triggerRef}
      className="relative"
      onMouseEnter={openSubmenu}
      onMouseLeave={closeSubmenu}
    >
      <div
        className={clsx(
          'w-full flex items-center justify-between gap-4 px-3 py-2 text-sm text-left transition-colors',
          item.disabled
            ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
        )}
      >
        <span className="flex items-center gap-2">
          {item.icon && <span className="w-4 h-4 flex-shrink-0">{item.icon}</span>}
          <span>{item.label}</span>
        </span>
        <svg
          className="w-4 h-4 text-gray-400 dark:text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>

      {isOpen && (
        <div
          ref={submenuRef}
          className={clsx(
            'fixed z-[60] min-w-[160px] py-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700',
            'animate-in fade-in-0 zoom-in-95 duration-100'
          )}
          style={{ top: position.top, left: position.left }}
          onMouseEnter={openSubmenu}
          onMouseLeave={closeSubmenu}
          role="menu"
          aria-label={`${item.label} submenu`}
        >
          <ContextMenuContent items={item.items} onSelect={handleSubmenuSelect} onClose={onClose} />
        </div>
      )}
    </div>
  );
}

// ============================================================================
// CONTEXT MENU CONTENT
// ============================================================================

interface ContextMenuContentProps {
  items: ContextMenuItemType[];
  onSelect: (item: ContextMenuItem) => void;
  onClose: () => void;
}

function ContextMenuContent({ items, onSelect, onClose }: ContextMenuContentProps) {
  return (
    <>
      {items.map((item) => {
        if (item.type === 'separator') {
          return <ContextMenuSeparatorComponent key={item.id} />;
        }

        if (item.type === 'submenu') {
          return (
            <ContextMenuSubmenuComponent
              key={item.id}
              item={item}
              onSelect={onSelect}
              onClose={onClose}
            />
          );
        }

        return (
          <ContextMenuItemComponent key={item.id} item={item as ContextMenuItem} onSelect={onSelect} />
        );
      })}
    </>
  );
}

// ============================================================================
// CONTEXT MENU
// ============================================================================

export interface ContextMenuProps {
  children: React.ReactNode;
  items: ContextMenuItemType[];
  onSelect?: (item: ContextMenuItem) => void;
  onOpenChange?: (isOpen: boolean) => void;
  disabled?: boolean;
  className?: string;
}

export function ContextMenu({
  children,
  items,
  onSelect,
  onOpenChange,
  disabled = false,
  className,
}: ContextMenuProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [position, setPosition] = React.useState<ContextMenuPosition>({ x: 0, y: 0 });
  const menuRef = React.useRef<HTMLDivElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const openMenu = (x: number, y: number) => {
    if (disabled) return;

    // Adjust position for viewport boundaries
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const menuWidth = 200; // Estimated width
    const menuHeight = items.filter((i) => i.type !== 'separator').length * 36 + items.filter((i) => i.type === 'separator').length * 9;

    let adjustedX = x;
    let adjustedY = y;

    if (x + menuWidth > viewportWidth) {
      adjustedX = viewportWidth - menuWidth - 8;
    }
    if (y + menuHeight > viewportHeight) {
      adjustedY = viewportHeight - menuHeight - 8;
    }
    if (adjustedX < 0) adjustedX = 8;
    if (adjustedY < 0) adjustedY = 8;

    setPosition({ x: adjustedX, y: adjustedY });
    setIsOpen(true);
    onOpenChange?.(true);
  };

  const closeMenu = React.useCallback(() => {
    setIsOpen(false);
    onOpenChange?.(false);
  }, [onOpenChange]);

  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    openMenu(event.clientX, event.clientY);
  };

  const handleSelect = (item: ContextMenuItem) => {
    onSelect?.(item);
    closeMenu();
  };

  // Close on click outside
  React.useEffect(() => {
    if (isOpen) {
      const handleClickOutside = (event: MouseEvent) => {
        if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
          closeMenu();
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, closeMenu]);

  // Close on escape
  React.useEffect(() => {
    if (isOpen) {
      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          closeMenu();
        }
      };

      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, closeMenu]);

  // Close on scroll
  React.useEffect(() => {
    if (isOpen) {
      const handleScroll = () => closeMenu();
      window.addEventListener('scroll', handleScroll, true);
      return () => window.removeEventListener('scroll', handleScroll, true);
    }
  }, [isOpen, closeMenu]);

  return (
    <>
      <div
        ref={containerRef}
        onContextMenu={handleContextMenu}
        className={className}
      >
        {children}
      </div>

      {isOpen && (
        <div
          ref={menuRef}
          className={clsx(
            'fixed z-50 min-w-[160px] py-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700',
            'animate-in fade-in-0 zoom-in-95 duration-100'
          )}
          style={{ top: position.y, left: position.x }}
          role="menu"
          aria-label="Context menu"
        >
          <ContextMenuContent items={items} onSelect={handleSelect} onClose={closeMenu} />
        </div>
      )}
    </>
  );
}

// ============================================================================
// CONTEXT MENU TRIGGER (Alternative API)
// ============================================================================

export interface ContextMenuTriggerProps {
  children: React.ReactNode;
  menuId: string;
  className?: string;
}

export interface ContextMenuContentWrapperProps {
  children?: React.ReactNode;
  items: ContextMenuItemType[];
  menuId: string;
  onSelect?: (item: ContextMenuItem) => void;
}

interface ContextMenuContextValue {
  openMenu: (menuId: string, position: ContextMenuPosition) => void;
  closeMenu: () => void;
  activeMenuId: string | null;
  position: ContextMenuPosition;
}

const ContextMenuContext = React.createContext<ContextMenuContextValue | null>(null);

export function useContextMenu() {
  const context = React.useContext(ContextMenuContext);
  if (!context) {
    throw new Error('useContextMenu must be used within a ContextMenuProvider');
  }
  return context;
}

export interface ContextMenuProviderProps {
  children: React.ReactNode;
}

export function ContextMenuProvider({ children }: ContextMenuProviderProps) {
  const [activeMenuId, setActiveMenuId] = React.useState<string | null>(null);
  const [position, setPosition] = React.useState<ContextMenuPosition>({ x: 0, y: 0 });

  const openMenu = React.useCallback((menuId: string, pos: ContextMenuPosition) => {
    setPosition(pos);
    setActiveMenuId(menuId);
  }, []);

  const closeMenu = React.useCallback(() => {
    setActiveMenuId(null);
  }, []);

  // Close on click outside
  React.useEffect(() => {
    if (activeMenuId) {
      const handleClickOutside = () => {
        closeMenu();
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [activeMenuId, closeMenu]);

  // Close on escape
  React.useEffect(() => {
    if (activeMenuId) {
      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          closeMenu();
        }
      };

      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [activeMenuId, closeMenu]);

  const value = React.useMemo(
    () => ({ openMenu, closeMenu, activeMenuId, position }),
    [openMenu, closeMenu, activeMenuId, position]
  );

  return (
    <ContextMenuContext.Provider value={value}>{children}</ContextMenuContext.Provider>
  );
}

export function ContextMenuTrigger({ children, menuId, className }: ContextMenuTriggerProps) {
  const { openMenu } = useContextMenu();

  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    openMenu(menuId, { x: event.clientX, y: event.clientY });
  };

  return (
    <div onContextMenu={handleContextMenu} className={className}>
      {children}
    </div>
  );
}

export function ContextMenuPortal({
  items,
  menuId,
  onSelect,
}: ContextMenuContentWrapperProps) {
  const { activeMenuId, position, closeMenu } = useContextMenu();

  const handleSelect = (item: ContextMenuItem) => {
    onSelect?.(item);
    closeMenu();
  };

  if (activeMenuId !== menuId) {
    return null;
  }

  return (
    <div
      className={clsx(
        'fixed z-50 min-w-[160px] py-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700',
        'animate-in fade-in-0 zoom-in-95 duration-100'
      )}
      style={{ top: position.y, left: position.x }}
      role="menu"
      aria-label="Context menu"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <ContextMenuContent items={items} onSelect={handleSelect} onClose={closeMenu} />
    </div>
  );
}

// ============================================================================
// USE CONTEXT MENU HOOK
// ============================================================================

export interface UseContextMenuOptions {
  onOpen?: (position: ContextMenuPosition) => void;
  onClose?: () => void;
}

export interface UseContextMenuReturn {
  isOpen: boolean;
  position: ContextMenuPosition;
  open: (x: number, y: number) => void;
  close: () => void;
  getContextMenuProps: () => {
    onContextMenu: (event: React.MouseEvent) => void;
  };
  getMenuProps: () => {
    style: React.CSSProperties;
    role: string;
  };
}

export function useContextMenuState(
  options: UseContextMenuOptions = {}
): UseContextMenuReturn {
  const { onOpen, onClose } = options;
  const [isOpen, setIsOpen] = React.useState(false);
  const [position, setPosition] = React.useState<ContextMenuPosition>({ x: 0, y: 0 });

  const open = React.useCallback(
    (x: number, y: number) => {
      setPosition({ x, y });
      setIsOpen(true);
      onOpen?.({ x, y });
    },
    [onOpen]
  );

  const close = React.useCallback(() => {
    setIsOpen(false);
    onClose?.();
  }, [onClose]);

  const getContextMenuProps = React.useCallback(
    () => ({
      onContextMenu: (event: React.MouseEvent) => {
        event.preventDefault();
        open(event.clientX, event.clientY);
      },
    }),
    [open]
  );

  const getMenuProps = React.useCallback(
    () => ({
      style: { top: position.y, left: position.x } as React.CSSProperties,
      role: 'menu' as const,
    }),
    [position]
  );

  // Close on click outside and escape
  React.useEffect(() => {
    if (isOpen) {
      const handleClickOutside = () => close();
      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === 'Escape') close();
      };

      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isOpen, close]);

  return { isOpen, position, open, close, getContextMenuProps, getMenuProps };
}
